import { Link } from 'react-router-dom';
import BlueDotsBackground from '../BlueDotsBackground';

export default function Assessment() {
  const handleScrollToExecutiveSummary = () => {
    const section = document.getElementById('executive-summary');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const challenges = [
    {
      text: 'Changes to URA formulas and rebate methodologies over time',
      icon: 'fas fa-arrows-rotate',
    },
    {
      text: 'Inconsistent program configurations across quarters',
      icon: 'fas fa-sliders',
    },
    {
      text: 'Claims processed with outdated URAs',
      icon: 'fas fa-clock-rotate-left',
    },
    {
      text: 'Unit of measure discrepancies between claims and rebate',
      icon: 'fas fa-ruler-combined',
    },
    {
      text: 'Government pricing calculation errors',
      icon: 'fas fa-calculator',
    },
    {
      text: 'Unresolved Medicaid disputes with uncertain financial outcomes',
      icon: 'fas fa-file-circle-question',
    },
    {
      text: 'Increasing 340B duplicate discount disputes',
      icon: 'fas fa-clone',
    },
    {
      text: 'Potential interest and penalty exposure on unresolved claims',
      icon: 'fas fa-percent',
    },
    {
      text: 'Reserve that may overstate or understate actual liability',
      icon: 'fas fa-scale-balanced',
    },
    {
      text: 'Limited visibility into future financial exposure',
      icon: 'fas fa-binoculars',
    },
  ];

  const solutionAreas = [
    {
      title: 'Medicaid Program Integrity',
      icon: 'fas fa-shield-halved',
      items: [
        'Program configuration',
        'Formula implementation',
        'Policy and filter integrity',
        'Effective dating controls',
        'Historical methodology consistency',
      ],
    },
    {
      title: 'Government Pricing Accuracy',
      icon: 'fas fa-calculator',
      items: [
        'AMP',
        'Best Price',
        'Base AMP',
        'CPI-adjusted calculations',
        'URAs',
      ],
    },
    {
      title: 'Claims Processing Accuracy',
      icon: 'fas fa-file-invoice-dollar',
      items: [
        'Applied URAs',
        'Units of measure',
        'Claim adjudication accuracy',
        'Rebate liability calculations',
        'Owed versus paid amounts',
      ],
    },
    {
      title: 'Dispute & Reserve Optimization',
      icon: 'fas fa-scale-balanced',
      items: [
        'Outstanding Medicaid disputes',
        '340B duplicate discount disputes',
        'Probability of payment versus dismissal',
        'Interest exposure',
        'Reserve adequacy and optimization opportunities',
      ],
    },
  ];

  const scopeServices = [
    {
      number: 1,
      title: 'Program & Formula Integrity Assessment',
      description:
        'Review all Medicaid rebate programs to ensure formula consistency over time, proper implementation of policy changes, accurate configuration of rebate methodologies, consistent use of pricing inputs, and correct effective dating and rule application.',
      deliverable: 'Program & URA Formula Analysis Report',
      includes: [
        'Program inventory',
        'Formula validation findings',
        'Historical configuration review',
        'Control gap analysis',
        'Recommended corrective actions',
      ],
    },
    {
      number: 2,
      title: 'Policy & Configuration Integrity Assessment',
      description:
        'Analyze policies, filters, rules, and program logic to confirm calculation integrity and system consistency.',
      deliverable: 'Policy Formula & Filter Integrity Report',
      includes: [
        'Policy configuration review',
        'Filter validation',
        'Effective dating verification',
        'Control observations',
        'Risk ranking of identified issues',
      ],
    },
    {
      number: 3,
      title: 'Government Pricing Validation',
      description:
        'Kernel independently recalculates and validates Medicaid pricing (MAMP, QAMP, Best Price, Base AMP) for the last 3 years.',
      deliverable: 'Government Pricing Validation Report',
      includes: [
        'Independent calculations',
        'Variance analysis',
        'Root cause identification',
        'Financial impact assessment',
        'Prioritized recommendations',
      ],
    },
    {
      number: 4,
      title: 'URA Calculation Assessment',
      description:
        'Independently recreate and validate URAs across all applicable Medicaid rebate programs, reviewing formula correctness, pricing input accuracy, CPI adjustment logic, program applicability, and quarter-over-quarter consistency.',
      deliverable: 'URA Calculation Analysis Report',
      includes: [
        'Quarter-level comparison',
        'Variance assessment',
        'Compliance observations',
        'Exposure quantification',
      ],
    },
    {
      number: 5,
      title: 'Medicaid Claims Liability Assessment',
      description:
        'Evaluate all Medicaid claims to verify rebate accuracy and ensure liabilities have been calculated and paid appropriately, reviewing latest URA application, unit-of-measure alignment, claim adjudication accuracy, rebate calculations, and owed versus paid comparison.',
      deliverable: 'Claims Accuracy & Liability Report',
      includes: [
        'Claim-level analysis',
        'Liability reconciliation',
        'Underpayment identification',
        'Overpayment identification',
        'Financial exposure summary',
      ],
    },
    {
      number: 6,
      title: 'Unit of Measure Integrity Assessment',
      description:
        'Validate alignment between Medicaid claims and approved unit definitions, reviewing ML versus vial conversions, package size inconsistencies, product-level unit mapping, historical unit changes, and unit utilization anomalies.',
      deliverable: 'Unit of Measure Integrity Report',
      includes: [
        'Unit mismatch analysis',
        'Financial impact estimates',
        'Corrective action recommendations',
      ],
    },
    {
      number: 7,
      title: 'Medicaid Dispute & Reserve Optimization Assessment',
      description:
        'A specialized review of outstanding Medicaid disputes and associated financial reserves. This assessment provides an objective evaluation of the organization\u2019s true exposure arising from disputed rebate invoices and identifies opportunities to optimize reserve levels.',
      deliverable: 'Medicaid Dispute & Reserve Optimization Report',
      includes: [
        'Open dispute inventory',
        'Aging dashboard',
        'Risk categorization',
        'Expected settlement analysis',
        'Reserve adequacy assessment',
        'Interest exposure estimates',
        'Recommended reserve range',
        'Executive-level exposure summary',
      ],
    },
  ];

  const executiveOutputs = [
    'Total Medicaid liability exposure',
    'Calculation accuracy score',
    'Government pricing risk summary',
    'Claims processing risk assessment',
    'Open dispute exposure',
    '340B duplicate discount exposure',
    'Interest liability estimates',
    'Recommended reserve adjustments',
    'Prioritized remediation roadmap',
  ];

  const faqs = [
    {
      question: 'What is the cost?',
      answer:
        'The assessment is priced at $100K, with a clear scope and executive-ready deliverables included.',
    },
    {
      question: 'What is the duration?',
      answer:
        'The engagement is designed to be completed in about 1 month, so your team gets clarity quickly without a long project cycle.',
    },
    {
      question:
        'How does Kernel obtain and guarantee the accuracy of the data?',
      answer:
        'Once secure connectivity is established to your Revenue Management System of Record, OpenPharma plugins ingest, migrate, and validate data completeness and accuracy before the assessment begins.',
    },
  ];

  const testimonials = [
    {
      quote:
        "OpenPharma has provided a level of visibility into our operations that we simply didn't have before. The platform helps us monitor performance, identify trends, and address challenges proactively, allowing our team to make faster, more informed decisions with greater confidence.",
      author: 'Reimbursement Director, Top 20 Pharmaceutical Company',
    },
    {
      quote:
        "OpenPharma's dispute reporting capabilities have been instrumental in improving our rebate dispute resolution process. The detailed insights and supporting data have enabled us to build stronger working relationships with state agencies, resulting in more productive discussions and a higher rate of disputes being resolved in our favor.",
      author: 'Medicaid Analyst',
    },
    {
      quote:
        'OpenPharma has enhanced our ability to monitor and manage rebate disputes while providing greater confidence in the accuracy of our reserves. The increased transparency and insights have been invaluable to our organization.',
      author: 'VP of Market Access',
    },
  ];

  const clientBenefits = [
    'Improve Medicaid rebate compliance',
    'Increase confidence in government pricing calculations',
    'Identify rebate underpayments and overpayments',
    'Enhance Model N and rebate system controls',
    'Reduce CMS, OIG, and audit exposure',
    'Quantify financial risk with greater precision',
    'Improve reserve accuracy and financial reporting',
    'Assess likelihood of dispute resolution outcomes',
    'Quantify 340B duplicate discount exposure',
    'Minimize interest and penalty liabilities',
    'Strengthen operational and compliance controls',
    'Improve forecasting and accrual methodologies',
  ];

  return (
    <>
      <BlueDotsBackground />

      <div className="text-white px-6 space-y-24">
        {/* Hero */}
        <section className="min-h-screen text-center max-w-5xl mx-auto flex flex-col items-center justify-center">
          <p className="text-sm md:text-base font-semibold tracking-[0.16em] uppercase text-[#464feb] mb-4">
            Medicaid Financial Integrity & Reserve Optimization Assessment
          </p>

          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Know Your True Medicaid Exposure
          </h1>

          <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-10">
            Independent validation of rebate liabilities, government pricing,
            dispute exposure, and reserve adequacy, giving leadership a clear,
            defensible view of financial risk and compliance readiness.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center mt-2 mb-8">
            <Link
              to="/contact"
              className="inline-block bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-xl font-semibold transition"
            >
              Request an Assessment
            </Link>

            <button
              type="button"
              onClick={handleScrollToExecutiveSummary}
              className="inline-block bg-transparent border border-blue-500 text-blue-300 hover:bg-blue-500/10 px-8 py-4 rounded-xl font-semibold transition"
            >
              View Executive Summary
            </button>
          </div>
        </section>

        {/* Executive Summary */}
        <section
          id="executive-summary"
          className="max-w-5xl mx-auto scroll-mt-28"
        >
          <h2 className="text-3xl font-bold text-center mb-8">
            Executive Summary
          </h2>

          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8">
            <p className="mb-4">
              Medicaid rebate programs have become increasingly complex due to
              evolving regulatory requirements, changing rebate methodologies,
              340B duplicate discount challenges, growing dispute volumes, and
              heightened scrutiny of government pricing calculations.
            </p>

            <p className="mb-4">
              The Medicaid Financial Integrity & Reserve Optimization Assessment
              provides pharmaceutical manufacturers with an independent
              validation of Medicaid rebate calculations, government pricing
              methodologies, claims processing, dispute management practices,
              and reserve adequacy.
            </p>

            <p>
              Leveraging advanced analytics, government pricing expertise, and
              proprietary assessment methodologies, Kernel identifies compliance
              risks, financial exposure, operational weaknesses, and reserve
              optimization opportunities. The result is a comprehensive view of
              Medicaid liability, helping organizations improve compliance,
              reduce audit risk, and strengthen financial controls.
            </p>
          </div>
        </section>

        {/* Key Business Challenges */}
        <section className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Key Business Challenges
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            {challenges.map((challenge, index) => (
              <div
                key={index}
                className="bg-gray-900 border border-gray-700 rounded-xl p-5 flex items-start gap-3"
              >
                <i className={`${challenge.icon} text-[#464feb] mt-1`}></i>
                <p>{challenge.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Solution Overview */}
        <section className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">
            Solution Overview
          </h2>

          <p className="text-center text-gray-300 max-w-3xl mx-auto mb-8">
            Kernel conducts an end-to-end assessment of Medicaid rebate
            operations, validating:
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {solutionAreas.map((area, index) => (
              <div
                key={index}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6"
              >
                <i className={`${area.icon} text-blue-400 text-3xl mb-4`}></i>

                <h3 className="text-xl font-semibold mb-4">{area.title}</h3>

                <ul className="space-y-2 text-gray-300">
                  {area.items.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Scope of Services */}
        <section
          id="scope-of-services"
          className="max-w-6xl mx-auto scroll-mt-28"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            Scope of Services
          </h2>

          <div className="space-y-8">
            {scopeServices.map((service, index) => (
              <div
                key={index}
                className="bg-gray-900 border border-gray-700 rounded-2xl p-8"
              >
                <h3 className="text-xl font-semibold mb-4">
                  <span className="text-blue-400">{service.number}.</span>{' '}
                  {service.title}
                </h3>

                <p className="text-gray-300 mb-6">{service.description}</p>

                <div className="bg-black/30 border border-gray-800 rounded-xl p-6">
                  <h4 className="text-blue-400 font-semibold mb-4">
                    Deliverable: {service.deliverable}
                  </h4>

                  <ul className="grid md:grid-cols-2 gap-2 text-gray-300">
                    {service.includes.map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Roles */}
        <section className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Roles & Responsibilities
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-4 text-blue-400">
                Customer Responsibilities
              </h3>

              <ul className="space-y-2">
                <li>• Provide required system access</li>
                <li>• Identify key stakeholders</li>
                <li>• Participate in kickoff and review meetings</li>
                <li>• Validate assessment results</li>
              </ul>
            </div>

            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-4 text-blue-400">
                Kernel / OpenPharma Responsibilities
              </h3>

              <ul className="space-y-2">
                <li>• Configure OpenPharma</li>
                <li>• Execute validation and variance assessments</li>
                <li>• Deliver reporting and recommendations</li>
                <li>• Present final assessment findings</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Executive Dashboard & Risk Summary */}
        <section className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Executive Dashboard & Risk Summary
          </h2>

          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8">
            <p className="mb-6 text-gray-300">
              Kernel consolidates findings into an executive report designed for
              Finance Leadership, Government Pricing Teams, Market Access
              Leadership, Compliance Officers, Internal Audit, and External
              Auditors.
            </p>

            <h3 className="text-xl font-semibold mb-4 text-blue-400">
              Executive Outputs
            </h3>

            <div className="grid md:grid-cols-2 gap-4">
              {executiveOutputs.map((output, index) => (
                <div
                  key={index}
                  className="bg-black/30 border border-gray-800 rounded-lg p-4"
                >
                  {output}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Client Benefits */}
        <section className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Client Benefits
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            {clientBenefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gray-900 border border-gray-700 rounded-xl p-5 flex items-start gap-3"
              >
                <i className="fas fa-circle-check text-green-400 mt-1"></i>
                <p>{benefit}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-gray-900 border border-gray-700 rounded-xl p-5"
              >
                <summary className="flex items-center justify-between font-semibold cursor-pointer list-none">
                  <span>{faq.question}</span>
                  <i className="fas fa-chevron-down text-blue-400 ml-4 transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-300">{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            What Our Customers Say
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gray-900 border border-gray-700 rounded-2xl p-8 flex flex-col"
              >
                <i className="fas fa-quote-left text-blue-400 text-2xl mb-4"></i>

                <p className="text-gray-300 italic flex-grow">
                  {testimonial.quote}
                </p>

                <p className="mt-6 font-semibold text-blue-400">
                  {testimonial.author}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Assessment Outcomes */}
        <section className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Assessment Outcomes
          </h2>

          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8">
            <p className="mb-6 text-gray-300">
              The Medicaid Financial Integrity & Reserve Optimization Assessment
              enables organizations to:
            </p>

            <ul className="space-y-3 text-gray-300">
              <li>
                <span className="font-semibold text-blue-400">
                  Validate Medicaid rebate liabilities
                </span>{' '}
                through independent verification of URAs, government pricing
                calculations, and claims processing.
              </li>
              <li>
                <span className="font-semibold text-blue-400">
                  Identify financial leakage
                </span>{' '}
                resulting from rebate overpayments, underpayments, calculation
                discrepancies, or unit-of-measure inconsistencies.
              </li>
              <li>
                <span className="font-semibold text-blue-400">
                  Strengthen compliance readiness
                </span>{' '}
                by confirming that rebate calculations, policies, and program
                configurations are aligned with current regulatory requirements.
              </li>
              <li>
                <span className="font-semibold text-blue-400">
                  Improve confidence in government pricing data
                </span>{' '}
                through independent validation of AMP, Best Price, MAMP, QAMP
                and related calculations.
              </li>
              <li>
                <span className="font-semibold text-blue-400">
                  Quantify outstanding dispute exposure
                </span>{' '}
                by assessing open Medicaid disputes and determining the
                likelihood of payment, settlement, or dismissal.
              </li>
              <li>
                <span className="font-semibold text-blue-400">
                  Evaluate 340B duplicate discount risk
                </span>{' '}
                and understand the potential financial impact of unresolved
                duplicate discount disputes.
              </li>
              <li>
                <span className="font-semibold text-blue-400">
                  Estimate interest and penalty exposure
                </span>{' '}
                associated with disputed invoices, aged receivables, and
                potential state repayment obligations.
              </li>
              <li>
                <span className="font-semibold text-blue-400">
                  Optimize reserve methodologies
                </span>{' '}
                by aligning reserve levels with probability-weighted dispute
                outcomes and historical resolution patterns.
              </li>
              <li>
                <span className="font-semibold text-blue-400">
                  Enhance financial reporting accuracy
                </span>{' '}
                through a more defensible and auditable approach to Medicaid
                liability accruals and reserves.
              </li>
              <li>
                <span className="font-semibold text-blue-400">
                  Provide executive visibility into risk
                </span>{' '}
                through a consolidated view of compliance exposure, financial
                liability, dispute status, and reserve sufficiency.
              </li>
              <li>
                <span className="font-semibold text-blue-400">
                  Prioritize remediation efforts
                </span>{' '}
                based on quantified financial impact, compliance risk, and
                operational improvement opportunities.
              </li>
              <li>
                <span className="font-semibold text-blue-400">
                  Establish a defensible framework for audits and inquiries
                </span>{' '}
                from internal audit, external auditors, CMS, state Medicaid
                agencies, and other stakeholders.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-blue-400 mt-8 mb-4">
              Bottom Line
            </h3>

            <p className="mb-4 text-gray-300">
              At the conclusion of the assessment, leadership will have a clear
              understanding of:
            </p>

            <ul className="space-y-2 text-gray-300">
              <li>• The accuracy of current Medicaid rebate liabilities.</li>
              <li>• The adequacy of existing reserves.</li>
              <li>
                • The financial exposure associated with open disputes and 340B
                duplicate discount claims.
              </li>
              <li>• The potential impact of interest and penalties.</li>
              <li>
                • The actions required to reduce risk, improve compliance, and
                optimize financial outcomes.
              </li>
            </ul>
          </div>
        </section>

        {/* Final CTA */}
        <section className="text-center max-w-4xl mx-auto">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-10">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Understand Your True Medicaid Exposure?
            </h2>

            <p className="text-gray-300 mb-8">
              Gain an independent, defensible view of your Medicaid liability,
              government pricing accuracy, dispute exposure, and reserve
              adequacy.
            </p>

            <Link
              to="/contact"
              className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-xl font-semibold transition inline-block"
            >
              Request an Assessment
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
